/**
 * Error Handling Utilities
 */

import { AppError } from './errors'
import { ErrorHandler } from './handler'

/**
 * Async error wrapper for functions
 * Automatically catches and normalizes errors
 */
export function asyncHandler<T = any>(
  fn: (...args: any[]) => Promise<T>
): (...args: any[]) => Promise<T> {
  return async (...args: any[]): Promise<T> => {
    try {
      return await fn(...args)
    } catch (error) {
      throw ErrorHandler.normalize(error)
    }
  }
}

/**
 * Try-catch wrapper that returns [data, error] tuple
 * Similar to Go's error handling pattern
 */
export async function tryAsync<T>(
  fn: () => Promise<T>
): Promise<[T | null, AppError | null]> {
  try {
    const data = await fn()
    return [data, null]
  } catch (error) {
    const appError = ErrorHandler.normalize(error)
    return [null, appError]
  }
}

/**
 * Retry with exponential backoff
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number
    initialDelay?: number
    maxDelay?: number
    factor?: number
    shouldRetry?: (error: AppError, attempt: number) => boolean
    onRetry?: (error: AppError, attempt: number) => void
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    factor = 2,
    shouldRetry = (error) => ErrorHandler.isRetriable(error),
    onRetry
  } = options

  let lastError: AppError
  let delay = initialDelay

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = ErrorHandler.normalize(error)
      
      if (attempt === maxRetries - 1 || !shouldRetry(lastError, attempt)) {
        throw lastError
      }

      if (onRetry) {
        onRetry(lastError, attempt + 1)
      }

      await new Promise(resolve => setTimeout(resolve, delay))
      delay = Math.min(delay * factor, maxDelay)
    }
  }

  throw lastError!
}

/**
 * Circuit breaker pattern
 */
export class CircuitBreaker<T> {
  private failures = 0
  private lastFailureTime = 0
  private state: 'closed' | 'open' | 'half-open' = 'closed'

  constructor(
    private readonly options: {
      threshold?: number
      timeout?: number
      resetTimeout?: number
    } = {}
  ) {
    this.options.threshold = options.threshold || 5
    this.options.timeout = options.timeout || 60000 // 1 minute
    this.options.resetTimeout = options.resetTimeout || 30000 // 30 seconds
  }

  async execute(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.options.resetTimeout!) {
        this.state = 'half-open'
      } else {
        throw new AppError('Circuit breaker is open', {
          code: 'CIRCUIT_BREAKER_OPEN',
          statusCode: 503
        })
      }
    }

    try {
      const result = await operation()
      this.onSuccess()
      return result
    } catch (error) {
      this.onFailure()
      throw error
    }
  }

  private onSuccess() {
    this.failures = 0
    this.state = 'closed'
  }

  private onFailure() {
    this.failures++
    this.lastFailureTime = Date.now()

    if (this.failures >= this.options.threshold!) {
      this.state = 'open'
    }
  }

  reset() {
    this.failures = 0
    this.state = 'closed'
    this.lastFailureTime = 0
  }

  getState() {
    return {
      state: this.state,
      failures: this.failures,
      lastFailureTime: this.lastFailureTime
    }
  }
}

/**
 * Timeout wrapper
 */
export async function withTimeout<T>(
  operation: () => Promise<T>,
  timeoutMs: number,
  timeoutError?: string
): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new AppError(
        timeoutError || `Operation timed out after ${timeoutMs}ms`,
        {
          code: 'TIMEOUT_ERROR',
          statusCode: 408
        }
      ))
    }, timeoutMs)
  })

  return Promise.race([operation(), timeoutPromise])
}

/**
 * Batch error collector
 * Useful for collecting multiple validation errors
 */
export class ErrorCollector {
  private errors: AppError[] = []

  add(error: unknown) {
    this.errors.push(ErrorHandler.normalize(error))
  }

  hasErrors(): boolean {
    return this.errors.length > 0
  }

  getErrors(): AppError[] {
    return this.errors
  }

  throwIfAny(message = 'Multiple errors occurred') {
    if (this.hasErrors()) {
      throw new AppError(message, {
        code: 'MULTIPLE_ERRORS',
        statusCode: 400,
        context: {
          metadata: {
            errors: this.errors.map(e => e.toJSON())
          }
        }
      })
    }
  }

  clear() {
    this.errors = []
  }
}

/**
 * Create a debounced error handler
 * Useful for preventing error spam
 */
export function createDebouncedErrorHandler(
  handler: (error: AppError) => void,
  delay = 1000
): (error: unknown) => void {
  let timeoutId: NodeJS.Timeout | null = null
  let lastError: AppError | null = null

  return (error: unknown) => {
    const appError = ErrorHandler.normalize(error)
    lastError = appError

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      if (lastError) {
        handler(lastError)
        lastError = null
      }
      timeoutId = null
    }, delay)
  }
}